<template>
  <Teleport v-if="hasTutorial && targetReady" :to="targetSelector">
    <button
      type="button"
      class="page-tutorial-launcher"
      data-tour="page-tutorial-button"
      :title="buttonTitle"
      :aria-label="buttonTitle"
      @click="startManualTutorial"
    >
      <span class="page-tutorial-symbol">?</span>
      <span>Aide</span>
    </button>
  </Teleport>
</template>

<script>
import {
  destroyActiveTutorial,
  getPageTutorialDefinition,
  isPageTutorialActive,
  startPageTutorial,
} from '@/services/pageTutorials';

const AUTO_START_DELAY = 850;

const versionMapFrom = (settings, key) => {
  const tutorial = settings?.tutorial && typeof settings.tutorial === 'object'
    ? settings.tutorial
    : settings;
  const versionMap = tutorial?.[key];
  return versionMap && typeof versionMap === 'object' && !Array.isArray(versionMap) ? versionMap : {};
};

const seenToursFrom = (settings) => versionMapFrom(settings, 'seen_tours');

const seenZonesFrom = (settings) => versionMapFrom(settings, 'seen_zones');

const localVersionMapKey = (user, name) => user?.id ? `suite241_tutorial_${name}_${user.id}` : null;

const readLocalVersionMap = (user, name) => {
  const key = localVersionMapKey(user, name);
  if (!key) return {};

  try {
    return JSON.parse(window.localStorage.getItem(key) || '{}');
  } catch {
    return {};
  }
};

const writeLocalVersionMap = (user, name, versionMap) => {
  const key = localVersionMapKey(user, name);
  if (!key) return;

  window.localStorage.setItem(key, JSON.stringify(versionMap));
};

export default {
  name: 'PageTutorialLauncher',
  data() {
    return {
      autoStartTimer: null,
      hasLoadedSettings: false,
      targetId: `suite241-page-tutorial-target-${Math.random().toString(36).slice(2)}`,
      targetReady: false,
      locallySeenTours: {},
      locallySeenZones: {},
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },
    currentDefinition() {
      return getPageTutorialDefinition(this.$route.name);
    },
    hasTutorial() {
      return Boolean(this.currentUser && this.currentDefinition);
    },
    tutorialSettings() {
      return this.$store.getters['userSettings/tutorial'] || {};
    },
    buttonTitle() {
      return this.currentDefinition
        ? `Revoir le tutoriel de la page ${this.currentDefinition.label}`
        : 'Revoir le tutoriel de la page';
    },
    targetSelector() {
      return `#${this.targetId}`;
    },
  },
  watch: {
    '$route.fullPath'() {
      destroyActiveTutorial(false);
      this.prepareButtonTarget();
      this.scheduleAutoStart();
    },
    tutorialSettings: {
      deep: true,
      handler() {
        this.scheduleAutoStart();
      },
    },
    currentUser() {
      this.loadSettingsAndSchedule();
    },
  },
  mounted() {
    this.prepareButtonTarget();
    this.loadSettingsAndSchedule();
  },
  beforeUnmount() {
    this.clearAutoStartTimer();
    this.removeButtonTarget();
    destroyActiveTutorial(false);
  },
  methods: {
    async loadSettingsAndSchedule() {
      if (!this.currentUser) return;

      this.locallySeenTours = readLocalVersionMap(this.currentUser, 'seen');
      this.locallySeenZones = readLocalVersionMap(this.currentUser, 'seen_zones');
      await this.$store.dispatch('userSettings/load').catch(() => undefined);
      this.hasLoadedSettings = true;
      this.scheduleAutoStart();
    },
    prepareButtonTarget() {
      this.targetReady = false;
      this.removeButtonTarget();

      this.$nextTick(() => {
        window.setTimeout(() => {
          if (!this.hasTutorial) return;

          const pageContent = document.querySelector('[data-tour="page-content"]');
          const heading = pageContent?.querySelector('h1, h2, .h1, .h2, .h3, .h4');
          if (!heading) return;

          const target = document.createElement('span');
          target.id = this.targetId;
          target.className = 'page-tutorial-inline-target';
          heading.appendChild(target);
          this.targetReady = true;
        }, 80);
      });
    },
    removeButtonTarget() {
      const target = document.getElementById(this.targetId);
      if (target) {
        target.remove();
      }
    },
    clearAutoStartTimer() {
      if (this.autoStartTimer) {
        window.clearTimeout(this.autoStartTimer);
        this.autoStartTimer = null;
      }
    },
    scheduleAutoStart() {
      this.clearAutoStartTimer();

      if (!this.hasLoadedSettings || !this.hasTutorial || this.tutorialSettings.auto_start === false) {
        return;
      }

      if (this.hasSeenCurrentTutorial()) {
        return;
      }

      if (isPageTutorialActive()) {
        return;
      }

      this.autoStartTimer = window.setTimeout(() => {
        const started = startPageTutorial(this.$route.name, {
          seenZoneKeys: this.currentSeenZoneKeys(),
          onFinished: (_definition, presentedZoneKeys) => this.markCurrentTutorialAsSeen(presentedZoneKeys),
        });
        this.autoStartTimer = null;

        if (!started) {
          this.markCurrentTutorialAsSeen();
        }
      }, AUTO_START_DELAY);
    },
    hasSeenCurrentTutorial() {
      const definition = this.currentDefinition;
      if (!definition) return true;

      const seenTours = seenToursFrom(this.tutorialSettings);
      return Number(seenTours[definition.key] || 0) >= definition.version
        || Number(this.locallySeenTours[definition.key] || 0) >= definition.version;
    },
    currentSeenZoneKeys() {
      const seenZones = {
        ...seenZonesFrom(this.tutorialSettings),
        ...this.locallySeenZones,
      };

      return Object.entries(seenZones)
        .filter(([, version]) => Number(version || 0) >= 1)
        .map(([key]) => key);
    },
    async markCurrentTutorialAsSeen(presentedZoneKeys = []) {
      const definition = this.currentDefinition;
      if (!definition) return;

      this.clearAutoStartTimer();
      const uniquePresentedZoneKeys = [...new Set(presentedZoneKeys)]
        .filter((zoneKey) => typeof zoneKey === 'string' && zoneKey.length > 0);

      if (!this.hasSeenCurrentTutorial()) {
        this.locallySeenTours = {
          ...this.locallySeenTours,
          [definition.key]: definition.version,
        };
        writeLocalVersionMap(this.currentUser, 'seen', this.locallySeenTours);
      }

      if (uniquePresentedZoneKeys.length) {
        this.locallySeenZones = uniquePresentedZoneKeys.reduce((seenZones, zoneKey) => ({
          ...seenZones,
          [zoneKey]: definition.version,
        }), { ...this.locallySeenZones });
        writeLocalVersionMap(this.currentUser, 'seen_zones', this.locallySeenZones);
      }

      const currentSettings = this.$store.getters['userSettings/settings'] || {};
      const seenTours = {
        ...seenToursFrom(currentSettings),
        [definition.key]: definition.version,
      };
      const seenZones = uniquePresentedZoneKeys.reduce((zones, zoneKey) => ({
        ...zones,
        [zoneKey]: definition.version,
      }), { ...seenZonesFrom(currentSettings) });

      await this.$store.dispatch('userSettings/save', {
        silent: true,
        settings: {
          ...currentSettings,
          tutorial: {
            ...(currentSettings.tutorial || {}),
            auto_start: currentSettings.tutorial?.auto_start !== false,
            seen_tours: seenTours,
            seen_zones: seenZones,
          },
        },
      }).catch(() => undefined);
    },
    startManualTutorial() {
      this.clearAutoStartTimer();
      startPageTutorial(this.$route.name, {
        seenZoneKeys: this.currentSeenZoneKeys(),
        onFinished: (_definition, presentedZoneKeys) => this.markCurrentTutorialAsSeen(presentedZoneKeys),
      });
    },
  },
};
</script>
